import React, { useState, useMemo } from 'react';
import { X, ChevronUp, ChevronDown, MapPin, Mountain, Search } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';
import { StationInfo } from '../../services/stationService';

interface StationListModalProps {
  isOpen: boolean;
  onClose: () => void;
  stations: StationInfo[];
  selectedRegion: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  onStationSelect: (station: StationInfo) => void;
}

type SortKey = 'name' | 'station' | 'latitude' | 'longitude' | 'elevation';
type SortDirection = 'asc' | 'desc';

const StationListModal: React.FC<StationListModalProps> = ({
  isOpen,
  onClose,
  stations,
  selectedRegion,
  onStationSelect,
}) => {
  const { theme } = useTheme();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and sort stations
  const filteredAndSortedStations = useMemo(() => {
    const filtered = stations.filter(
      (station) =>
        station.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        station.station.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return filtered.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [stations, searchTerm, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '90vw',
          maxWidth: '1000px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                marginBottom: '4px',
              }}
            >
              Stations in {selectedRegion ? 'Selected Region' : 'All Regions'}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
              }}
            >
              {filteredAndSortedStations.length} stations found
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
          }}
        >
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme === 'dark' ? '#6B7280' : '#9CA3AF',
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Search stations by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                border: `2px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                outline: 'none',
                transition: 'all 0.2s ease',
                boxShadow:
                  theme === 'dark'
                    ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                    : '0 1px 3px rgba(0, 0, 0, 0.1)',
              }}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#3B82F6';
                (e.target as HTMLInputElement).style.boxShadow =
                  theme === 'dark'
                    ? '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.3)'
                    : '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.borderColor =
                  theme === 'dark' ? '#4B5563' : '#E5E7EB';
                (e.target as HTMLInputElement).style.boxShadow =
                  theme === 'dark'
                    ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                    : '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOver={(e) => {
                if (document.activeElement !== e.target) {
                  (e.target as HTMLInputElement).style.borderColor =
                    theme === 'dark' ? '#6B7280' : '#D1D5DB';
                }
              }}
              onMouseOut={(e) => {
                if (document.activeElement !== e.target) {
                  (e.target as HTMLInputElement).style.borderColor =
                    theme === 'dark' ? '#4B5563' : '#E5E7EB';
                }
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('name')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Name
                    <SortIcon column="name" />
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('station')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Station ID
                    <SortIcon column="station" />
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('latitude')}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px',
                    }}
                  >
                    Latitude
                    <SortIcon column="latitude" />
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('longitude')}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px',
                    }}
                  >
                    Longitude
                    <SortIcon column="longitude" />
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('elevation')}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px',
                    }}
                  >
                    Elevation (m)
                    <SortIcon column="elevation" />
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                    position: 'sticky',
                    top: 0,
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedStations.map((station, index) => (
                <tr
                  key={station.station}
                  style={{
                    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                    backgroundColor:
                      index % 2 === 0 ? 'transparent' : theme === 'dark' ? '#111827' : '#F9FAFB',
                  }}
                >
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                      fontWeight: '500',
                    }}
                  >
                    {station.name}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                    }}
                  >
                    {station.station}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                    }}
                  >
                    {station.latitude.toFixed(4)}°
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                    }}
                  >
                    {station.longitude.toFixed(4)}°
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                    }}
                  >
                    {station.elevation.toFixed(0)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => onStationSelect(station)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 10px',
                        backgroundColor: '#E34290',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#c73678';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#E34290';
                      }}
                    >
                      <MapPin size={12} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAndSortedStations.length === 0 && (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
              }}
            >
              <Mountain size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>No stations found</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
                Try adjusting your search or region selection
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StationListModal;
